'use client';

import { useState, useEffect } from 'react';

import { log } from 'src/utils/logger';

import { fsCall, fsSubscribe } from 'src/actions/firestore/dispatch';

const SERVICE_NAME = 'common/tracked-sources';

/**
 * **워크스페이스가 걸어 둔 것 세 갈래를 실시간으로 문다** — 설정(`brandSetting`) ·
 * 타겟 키워드 캠페인 · 시딩 캠페인.
 *
 * 🔑 **구독이다**(`fsSubscribe`) — 설정 팝업에서 고친 값이 다시 열지 않아도 화면에 온다.
 * ⚠️ 타겟 키워드만 **한 번 불러온 뒤 구독**한다 — 구독 첫 스냅샷이 늦게 오는 자리라
 *   그동안 화면이 「없음」으로 보인다.
 * ⚠️ 워크스페이스 id 가 없으면 셋 다 빈 값이다 — 부르는 쪽에서 로딩과 빈 상태를 가른다.
 * ⚠️ **안 볼 갈래는 안 문다**(`seeding: false`) — 구독은 살아 있는 리스너라, 화면이 안 쓰는
 *   것을 걸어 두면 그만큼 그냥 돈다.
 *
 * @param workspaceId 지금 워크스페이스 id
 * @param options `seeding` 을 거짓으로 주면 시딩 캠페인을 구독하지 않는다(기본은 문다)
 * @returns 살아 있는 워크스페이스 문서와 캠페인 두 벌
 */
export function useTrackedSourcesLoad(
  workspaceId: string | null | undefined,
  options?: { seeding?: boolean },
) {
  const withSeeding = options?.seeding !== false;
  const [liveWorkspace, setLiveWorkspace] = useState<Record<string, any> | null>(null);
  const [keywordCampaigns, setKeywordCampaigns] = useState<Record<string, any>[]>([]);
  const [influencerCampaigns, setInfluencerCampaigns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    if (!workspaceId) {
      setLiveWorkspace(null);
      setKeywordCampaigns([]);
      setInfluencerCampaigns([]);
      return undefined;
    }

    fsCall('keywordCampaign.byWorkspace.get', { workspaceId })
      .then((res) => setKeywordCampaigns(Array.isArray(res?.rows) ? res.rows : []))
      .catch((e) => {
        log.error(SERVICE_NAME, '타겟 키워드 캠페인 조회 실패', { err: e?.message ?? String(e) });
        setKeywordCampaigns([]);
      });

    const unsubs = [
      fsSubscribe('workspace.byId', { workspaceId }, {
        onChange: (doc: any) => setLiveWorkspace(doc ?? null),
      }),
      fsSubscribe('keywordCampaign.byWorkspace', { workspaceId }, {
        onChange: (rows: any) => setKeywordCampaigns(Array.isArray(rows) ? rows : []),
      }),
      ...(withSeeding
        ? [fsSubscribe('campaign.byWorkspace', { workspaceId }, {
            onChange: (rows: any) => setInfluencerCampaigns(Array.isArray(rows) ? rows : []),
          })]
        : []),
    ];

    return () => {
      unsubs.forEach((unsub) => {
        try { unsub?.(); } catch { /* no-op */ }
      });
    };
  }, [workspaceId, withSeeding]);

  return { liveWorkspace, keywordCampaigns, influencerCampaigns };
}
